import type { KnowledgeItem } from "@note-app/contracts";

export type CollectionViewSort =
  | "updated-desc"
  | "updated-asc"
  | "created-desc"
  | "title-asc";
export type CollectionViewGroup = "none" | "folder" | "status" | "kind";
export type CollectionViewDate = "all" | "7" | "30";
export type CollectionViewLayout = "grid" | "list";

export interface CollectionViewState {
  readonly query: string;
  readonly sort: CollectionViewSort;
  readonly folder: string;
  readonly tag: string;
  readonly date: CollectionViewDate;
  readonly group: CollectionViewGroup;
  readonly layout: CollectionViewLayout;
}

export interface KnowledgeItemGroup {
  readonly key: string;
  readonly items: readonly KnowledgeItem[];
}

export const DEFAULT_COLLECTION_VIEW: CollectionViewState = {
  query: "",
  sort: "updated-desc",
  folder: "",
  tag: "",
  date: "all",
  group: "none",
  layout: "grid",
};

export function applyCollectionView(
  items: readonly KnowledgeItem[],
  view: CollectionViewState,
  options: { readonly now?: number; readonly locale?: string } = {},
): KnowledgeItem[] {
  const now = options.now ?? Date.now();
  const locale = options.locale ?? "zh-CN";
  const query = normalizeCollectionQuery(view.query);
  const earliestUpdate = view.date === "all"
    ? undefined
    : now - Number(view.date) * 86_400_000;

  return items
    .filter((item) =>
      !query || normalizeCollectionQuery(item.title).includes(query),
    )
    .filter((item) =>
      !view.folder ||
      (view.folder === "__none"
        ? item.folderId === undefined
        : item.folderId === view.folder),
    )
    .filter((item) => !view.tag || item.tags.includes(view.tag))
    .filter((item) =>
      earliestUpdate === undefined || item.updatedAt >= earliestUpdate,
    )
    .sort(collectionViewComparator(view.sort, locale));
}

export function groupCollectionItems(
  items: readonly KnowledgeItem[],
  group: CollectionViewGroup,
): KnowledgeItemGroup[] {
  if (items.length === 0) return [];
  if (group === "none") return [{ key: "all", items: [...items] }];

  const groups = new Map<string, KnowledgeItem[]>();
  for (const item of items) {
    const status = item.properties["fengyue.status"];
    const key = group === "folder"
      ? `folder:${item.folderId ?? "none"}`
      : group === "status"
        ? `status:${typeof status === "string" && status ? status : "none"}`
        : `kind:${item.kind}`;
    groups.set(key, [...(groups.get(key) ?? []), item]);
  }
  return [...groups.entries()].map(([key, groupedItems]) => ({
    key,
    items: groupedItems,
  }));
}

export function activeCollectionViewFilterCount(
  view: CollectionViewState,
): number {
  return [
    view.sort !== DEFAULT_COLLECTION_VIEW.sort,
    Boolean(view.folder),
    Boolean(view.tag),
    view.date !== DEFAULT_COLLECTION_VIEW.date,
    view.group !== DEFAULT_COLLECTION_VIEW.group,
  ].filter(Boolean).length;
}

export function normalizeCollectionQuery(value: string): string {
  return value.normalize("NFKC").trim().toLocaleLowerCase();
}

function collectionViewComparator(
  sort: CollectionViewSort,
  locale: string,
): (left: KnowledgeItem, right: KnowledgeItem) => number {
  if (sort === "updated-asc") {
    return (left, right) =>
      left.updatedAt - right.updatedAt || left.id.localeCompare(right.id);
  }
  if (sort === "created-desc") {
    return (left, right) =>
      right.createdAt - left.createdAt || left.id.localeCompare(right.id);
  }
  if (sort === "title-asc") {
    return (left, right) =>
      left.title.localeCompare(right.title, locale) ||
      left.id.localeCompare(right.id);
  }
  return (left, right) =>
    right.updatedAt - left.updatedAt || left.id.localeCompare(right.id);
}
